import { useState, useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import { supabase, Usuario, supabaseUrl } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/base/ConfirmDialog';

export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [formData, setFormData] = useState({ nombre: '', usuario: '', password: '', rol: 'usuario' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Usuario | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsuarios = async () => {
    try {
      const { data } = await supabase.from('usuarios').select('*').order('created_at', { ascending: false });
      if (data) setUsuarios(data);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ nombre: '', usuario: '', password: '', rol: 'usuario' });
    setShowPassword(false);
    setShowForm(true);
  };

  const handleEdit = (usuario: Usuario) => {
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      usuario: usuario.usuario,
      password: '',
      rol: usuario.rol,
    });
    setShowPassword(false);
    setShowForm(true);
  };

  const invokeEdgeFunction = async (name: string, body: Record<string, unknown>) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token || '';
    const url = `${supabaseUrl}/functions/v1/${name}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json().catch(() => ({ error: 'Error de respuesta del servidor' }));

    if (!response.ok) {
      throw new Error(responseData?.error || responseData?.message || `Error ${response.status}`);
    }

    return responseData;
  };

  const handleSave = async () => {
    if (!formData.nombre.trim() || !formData.usuario.trim()) {
      showToast('Complete todos los campos obligatorios', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const result = await invokeEdgeFunction('update-user', {
          id: editingUser.id,
          nombre: formData.nombre.trim(),
          rol: formData.rol,
          password: formData.password.trim() || undefined,
        });

        if (!result?.success) {
          throw new Error(result?.error || 'Error al actualizar usuario');
        }

        showToast('Usuario actualizado correctamente', 'success');
      } else {
        if (!formData.password.trim()) {
          showToast('La contraseña es obligatoria', 'error');
          setSaving(false);
          return;
        }

        const result = await invokeEdgeFunction('create-user', {
          nombre: formData.nombre.trim(),
          usuario: formData.usuario.trim(),
          password: formData.password.trim(),
          rol: formData.rol,
        });

        if (!result?.success) {
          throw new Error(result?.error || 'Error al crear usuario');
        }

        showToast('Usuario creado correctamente', 'success');
      }

      setShowForm(false);
      await fetchUsuarios();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar el usuario';
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (usuario: Usuario) => {
    if (usuario.usuario === 'ticona') {
      showToast('No se puede eliminar al administrador principal', 'error');
      return;
    }
    setDeleteTarget(usuario);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    try {
      const result = await invokeEdgeFunction('delete-user', {
        id: deleteTarget.id,
        usuario: deleteTarget.usuario,
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Error al eliminar usuario');
      }

      showToast('Usuario eliminado correctamente', 'success');
      await fetchUsuarios();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al eliminar el usuario';
      showToast(message, 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Cargando usuarios...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === 'success' ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'
          }`}>
            {toast.message}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">Usuarios</h2>
            <p className="text-sm text-gray-500 mt-0.5">Administración de usuarios ({usuarios.length})</p>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
            Nuevo Usuario
          </button>
        </div>

        <div className="bg-white rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Nombre</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Usuario</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Contraseña</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Rol</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-[#F4F6F8]/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                          {u.nombre.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-[#111827]">{u.nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{u.usuario}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-gray-400 tracking-widest select-none" title="Contraseña almacenada de forma segura">••••••••</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        u.rol === 'super_admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-[#2563EB]/10 text-[#2563EB]'
                      }`}>
                        {u.rol === 'super_admin' ? 'Super Admin' : 'Usuario'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-400 hover:text-[#2563EB] transition-colors cursor-pointer"
                          title="Editar"
                        >
                          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                        {u.usuario !== 'ticona' && u.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDelete(u)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-400 hover:text-[#DC2626] transition-colors cursor-pointer"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {usuarios.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">
                      No hay usuarios registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={executeDelete}
          title={`¿Eliminar a ${deleteTarget?.nombre || 'usuario'}?`}
          message="El usuario será eliminado permanentemente del sistema."
          confirmText="Sí, eliminar"
          confirmColor="danger"
          icon="ri-delete-bin-line"
        />

        {showForm && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowForm(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[#111827]">
                      {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                    </h3>
                    <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] cursor-pointer">
                      <i className="ri-close-line text-gray-500 w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre Completo</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Usuario</label>
                    <input
                      type="text"
                      value={formData.usuario}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                      disabled={!!editingUser}
                      className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB] disabled:opacity-50"
                      placeholder="Ej: juan"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Contraseña {editingUser && '(dejar vacío para no cambiar)'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB] pr-10"
                        placeholder={editingUser ? '••••••••' : 'Contraseña'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      >
                        <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} w-4 h-4 flex items-center justify-center`}></i>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol</label>
                    <select
                      value={formData.rol}
                      onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                      className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB] cursor-pointer"
                    >
                      <option value="usuario">Usuario</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 bg-[#F4F6F8] text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                          <span>Guardando...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                          <span>{editingUser ? 'Actualizar' : 'Crear Usuario'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}